import { t } from '@lingui/core/macro';
import { CarAxleType, type CarTireSizeOptionRecord } from '@navio/nsdk';
import { NavioTireDimensionInput, NoddiCard, NoddiCheckbox, NoddiSelectableBox } from 'noddi-ui-web';
import { getAxleLabels } from './requestOfferOptions';
import { EMPTY_TIRE_SIZE_INPUT, getModelSizeInput, type TireAxle, type TireSizeInput } from './tireSizes';

type TireSizeGroup = {
  key: string;
  title: string;
  /** Axles without a registered size; never empty, cars with both are not asked. */
  missingAxles: TireAxle[];
  /** Sizes the car's model comes with, offered to pick from before typing one in. */
  modelSizes: CarTireSizeOptionRecord[];
  input: TireSizeInput;
};

type Props = {
  /** One per car and season whose size we don't know. */
  groups: TireSizeGroup[];
  onInputChange: (key: string, input: TireSizeInput) => void;
};

/** Shown only when a picked car has no size registered for the season. */
export const TireSizeStep = ({ groups, onInputChange }: Props) => (
  <div className='flex flex-col gap-2'>
    <p className='font-semibold'>{t`What size are your tires?`}</p>
    <p className='text-sm text-muted-foreground'>
      {t`You find the size on the side of the tire, for example 205/55R16. We save it on your car.`}
    </p>
    {groups.map(({ key, title, missingAxles, modelSizes, input }) => (
      <NoddiCard key={key} contentClassName='flex flex-col gap-4'>
        {/* Not the card title: that is sentence-cased, which lowercases the make, model and plate. */}
        <p className='font-semibold'>{title}</p>
        {modelSizes.length > 0 ? (
          <ModelSizes
            modelSizes={modelSizes}
            missingAxles={missingAxles}
            input={input}
            onChange={(next) => onInputChange(key, next)}
          />
        ) : (
          <TypedSizes missingAxles={missingAxles} input={input} onChange={(next) => onInputChange(key, next)} />
        )}
      </NoddiCard>
    ))}
  </div>
);

/** One box per size the car's model comes with, then "Another size" to type it in; one choice. */
const ModelSizes = ({
  modelSizes,
  missingAxles,
  input,
  onChange
}: {
  modelSizes: CarTireSizeOptionRecord[];
  missingAxles: TireAxle[];
  input: TireSizeInput;
  onChange: (input: TireSizeInput) => void;
}) => (
  <div className='flex flex-col gap-1'>
    {modelSizes.map(({ front, rear }, index) => (
      <NoddiSelectableBox
        key={`${front}:${rear}`}
        aria-pressed={input.choice === index}
        isSelected={input.choice === index}
        setIsSelected={() => onChange(getModelSizeInput(index, { front, rear }))}
      >
        {front === rear ? front : t`front ${front}, rear ${rear}`}
      </NoddiSelectableBox>
    ))}
    <NoddiSelectableBox
      aria-pressed={input.choice === 'TYPED'}
      isSelected={input.choice === 'TYPED'}
      setIsSelected={() => onChange({ ...EMPTY_TIRE_SIZE_INPUT, choice: 'TYPED' })}
    >
      {t`Another size`}
    </NoddiSelectableBox>
    {input.choice === 'TYPED' && (
      <div className='flex flex-col gap-4 pt-3 motion-safe:animate-in motion-safe:duration-200 motion-safe:fade-in motion-safe:slide-in-from-top-2'>
        <TypedSizes missingAxles={missingAxles} input={input} onChange={onChange} />
      </div>
    )}
  </div>
);

const TypedSizes = ({
  missingAxles,
  input,
  onChange
}: {
  missingAxles: TireAxle[];
  input: TireSizeInput;
  onChange: (input: TireSizeInput) => void;
}) => {
  const axleLabels = getAxleLabels();
  const setInput = (changes: Partial<TireSizeInput>) => onChange({ ...input, ...changes });

  if (missingAxles.length === 2) {
    return (
      <>
        <TireSizeField
          label={input.isStaggered ? axleLabels[CarAxleType.FRONT] : t`Tire size`}
          value={input.front}
          onChange={(front) => setInput({ front })}
        />
        <NoddiCheckbox
          label={t`The front and rear tires have different sizes`}
          checked={input.isStaggered}
          onCheckedChange={(isStaggered) => setInput({ isStaggered })}
        />
        {input.isStaggered && (
          <TireSizeField
            label={axleLabels[CarAxleType.REAR]}
            value={input.rear}
            onChange={(rear) => setInput({ rear })}
          />
        )}
      </>
    );
  }

  return missingAxles.map((axle) => (
    <TireSizeField
      key={axle}
      label={axleLabels[axle]}
      value={axle === CarAxleType.FRONT ? input.front : input.rear}
      onChange={(size) => setInput(axle === CarAxleType.FRONT ? { front: size } : { rear: size })}
    />
  ));
};

/** The size in the shared boxes, worded for the customer and checked against what a passenger car has. */
const TireSizeField = ({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <NavioTireDimensionInput
    label={label}
    value={value}
    onChange={onChange}
    validateTypicalRange
    translations={{
      width: t`Width`,
      profile: t`Profile`,
      rim: t`Rim`,
      widthAriaLabel: t`Width in millimeters`,
      profileAriaLabel: t`Profile in percent of the width`,
      rimAriaLabel: t`Rim diameter in inches`,
      widthOutsideTypicalRange: (min, max) => t`The width is usually between ${min} and ${max} mm.`,
      profileOutsideTypicalRange: (min, max) => t`The profile is usually between ${min} and ${max}.`,
      rimOutsideTypicalRange: (min, max) => t`The rim diameter is usually between ${min} and ${max} inches.`
    }}
  />
);
